import { useState, useEffect } from "react";
import "@/App.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Navbar from "./components/lws/Navbar";
import Footer from "./components/lws/Footer";
import { 
  HomePage, 
  EventsPage, 
  ChaptersPage, 
  ShopPage, 
  JournalPage, 
  AboutPage, 
  BlogPostPage, 
  HostingPage, 
  ChallengePage,
  ReadPage
} from "./components/lws/PageComponents";
import {
  LoginPage,
  RegisterPage,
  PenPalLandingPage,
  CreatePenPalProfilePage,
  BrowsePenPalsPage,
  MyPenPalProfilePage,
  PenPalConnectionsPage
} from "./components/lws/PenPalPages";
import MessagesPage, { ConversationsListPage } from "./components/lws/MessagesPage";
import LetterTrackingPage from "./components/lws/LetterTrackingPage";
import EmailPreferencesPage from "./components/lws/EmailPreferencesPage";
import { MembershipPage, MembershipSuccessPage } from "./components/lws/MembershipPage";
import CoursesPage from "./components/lws/CoursesPage";
import { ForwardingPortalPage, ForwardingSuccessPage } from "./components/lws/ForwardingPortalPage";
import AdminForwardingPage from "./components/lws/AdminForwardingPage";
import AdminReportsPage from "./components/lws/AdminReportsPage";
import PaymentHistoryPage from "./components/lws/PaymentHistoryPage";
import PostmasterApplicationPage from "./components/lws/PostmasterApplicationPage";
import AdminPostmasterPage from "./components/lws/AdminPostmasterPage";
import PromptDeckPage from "./components/lws/PromptDeckPage";
import SystemAdminPage from "./components/lws/SystemAdminPage";
import AdminResourcesPage from "./components/lws/AdminResourcesPage";
import AdminEventsPage from "./components/lws/AdminEventsPage";
import AdminSeasonalPostPage from "./components/lws/AdminSeasonalPostPage";
import EventsListPage from "./components/lws/EventsPage";
import ResourceLibraryPage from "./components/lws/ResourceLibraryPage";
import SubscriptionBoxPage from "./components/lws/SubscriptionBoxPage";
import SeasonalPostPage from "./components/lws/SeasonalPostPage";
import { WelcomeOnboarding, CoursesTooltipTour, useOnboarding } from "./components/lws/OnboardingWizard";
import { AnimatePresence } from "motion/react";

function AppContent() {
  const [currentPage, setCurrentPage] = useState("home");
  const [routeParams, setRouteParams] = useState(null);
  const { user, loading } = useAuth();
  const {
    showWelcome,
    showCoursesTour,
    completeWelcome,
    skipWelcome,
    startCoursesTour,
    completeCoursesTour,
    skipCoursesTour
  } = useOnboarding();

  // Simple routing based on hash
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "") || "home";
      
      if (hash.startsWith("blog-post/")) {
        setCurrentPage("blog-post");
        setRouteParams(hash.replace("blog-post/", ""));
      } else if (hash.startsWith("reset-password/")) {
        setCurrentPage("reset-password");
        setRouteParams(hash.replace("reset-password/", ""));
      } else if (hash.startsWith("penpal-messages/")) {
        setCurrentPage("penpal-messages");
        setRouteParams(hash.replace("penpal-messages/", ""));
      } else if (hash.startsWith("letter-tracking/")) {
        setCurrentPage("letter-tracking");
        setRouteParams(hash.replace("letter-tracking/", ""));
      } else if (hash.startsWith("membership-success")) {
        setCurrentPage("membership-success");
        setRouteParams(null);
      } else if (hash.startsWith("forwarding-success") || hash.startsWith("forwarding-credits-success")) {
        setCurrentPage("forwarding-success");
        setRouteParams(null);
      } else if (hash.startsWith("register/")) {
        setCurrentPage("register");
        setRouteParams(hash.replace("register/", ""));
      } else {
        setCurrentPage(hash);
        setRouteParams(null);
      }
      
      window.scrollTo(0, 0);
    };

    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();

    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case "home": return <HomePage />;
      case "events": return <EventsListPage />;
      case "chapters": return <ChaptersPage />;
      case "shop": return <ShopPage />;
      case "read": return <ReadPage />;
      case "journal": return <JournalPage />;
      case "about": return <AboutPage />;
      case "blog-post": return <BlogPostPage id={routeParams || ""} />;
      case "hosting": return <HostingPage />;
      case "challenge": return <ChallengePage />;
      case "box": return <SubscriptionBoxPage />;
      case "seasonal-post": return <SeasonalPostPage />;
      
      // Auth pages
      case "login": return <LoginPage onSuccess={() => window.location.hash = "#penpal"} />;
      case "register": return <RegisterPage signupSource={routeParams || "general"} onSuccess={() => window.location.hash = "#penpal-create"} />;
      
      // Pen Pal pages
      case "penpal": return <PenPalLandingPage />;
      case "penpal-create": return <CreatePenPalProfilePage />;
      case "penpal-browse": return <BrowsePenPalsPage />;
      case "penpal-profile": return <MyPenPalProfilePage />;
      case "penpal-connections": return <PenPalConnectionsPage />;
      case "penpal-inbox": return <ConversationsListPage />;
      case "penpal-messages": return <MessagesPage connectionId={routeParams} />;
      case "letter-tracking": return <LetterTrackingPage connectionId={routeParams} />;
      case "email-preferences": return <EmailPreferencesPage />;
      
      // Membership & Course pages
      case "membership": return <MembershipPage />;
      case "membership-success": return <MembershipSuccessPage />;
      case "courses": return <CoursesPage onMount={startCoursesTour} />;
      case "waitlist": return <HomePage />;
      
      // Private Forwarding pages
      case "forwarding": return <ForwardingPortalPage />;
      case "forwarding-success": return <ForwardingSuccessPage />;
      case "admin-forwarding": return <AdminForwardingPage />;
      
      // Admin pages
      case "admin-reports": return <AdminReportsPage />;
      case "admin-postmaster": return <AdminPostmasterPage />;
      case "admin-system": return <SystemAdminPage />;
      case "admin-resources": return <AdminResourcesPage />;
      case "admin-events": return <AdminEventsPage />;
      case "admin-seasonal-post": return <AdminSeasonalPostPage />;
      
      // Member pages
      case "resources": return <ResourceLibraryPage />;
      
      // Payment & Billing
      case "payment-history": return <PaymentHistoryPage />;
      
      // Postmaster Application
      case "hosting-application": return <PostmasterApplicationPage />;
      
      // Prompt Card Deck
      case "prompts": return <PromptDeckPage />;
      
      default: return <HomePage />;
    }
  };

  return (
    <div className="paper-texture min-h-screen" data-testid="app-container">
      <Navbar />
      <main className="pt-36">
        {renderPage()}
      </main>
      <Footer />
      
      {/* Onboarding Modals */}
      <AnimatePresence>
        {showWelcome && (
          <WelcomeOnboarding 
            onComplete={completeWelcome} 
            onSkip={skipWelcome} 
          />
        )}
      </AnimatePresence>
      
      {showCoursesTour && currentPage === "courses" && (
        <CoursesTooltipTour
          onComplete={completeCoursesTour}
          onSkip={skipCoursesTour}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
